
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form, useForm } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import SearchField from '@/components/common/SearchField.vue';

  export default defineComponent({
    name: 'finance-adjustment-creating',
    components: {
      SearchField,
      ErrorMessage,
      Form,
      Field,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const date = ref();
      const from = ref('');
      const to = ref('');
      const amount = ref<number | null>(0);
      const action = ref('transfer');
      const creditOrDebit = ref('');
      const { resetForm } = useForm();

      const showCreditDebit = computed(() => action.value === 'adjustment');
      const showToCustomer = computed(() => action.value === 'transfer');

      const actions = [
        { id: 'transfer', name: 'Transfer' },
        { id: 'adjustment', name: 'Adjustment' },
      ];

      const creditDebit = [
        { id: 'credit', name: 'Credit' },
        { id: 'debit', name: 'Debit' },
      ];

      const onStartDateChange = (selectedDate) => {
        date.value = selectedDate;
      };

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('adjustment Creating'), [
          translate('finance'),
        ]);
      });

      const validationSchema = computed(() =>
        Yup.object().shape({
          from: Yup.string()
            .notOneOf(['0'], () => translate('CUSTOMER_IS_REQUIRED_FIELD'))
            .required(() => translate('CUSTOMER_IS_REQUIRED_FIELD'))
            .label('Customer'),
          to: showToCustomer.value
            ? Yup.string()
                .notOneOf(['0'], () => translate('CUSTOMER_IS_REQUIRED_FIELD'))
                .required(() => translate('CUSTOMER_IS_REQUIRED_FIELD'))
                .label('Customer')
            : Yup.string().nullable(),
          creditOrDebit: showCreditDebit.value
            ? Yup.string()
                .required(() => translate('Debit Or Credit Is Required'))
                .label('Debit or Credit')
            : Yup.string().nullable(),
          amount: Yup.number()
            .typeError('Please enter a valid number')
            .required('Amount is required')
            .min(0, 'Amount must be greater than or equal to 0')
            .label('amount'),
        })
      );
      await store.dispatch(Actions.GET_ALL_CUSTOMERS);

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        if (showCreditDebit.value) {
          values.creditOrDebit = creditOrDebit.value;
        } else {
          delete values.creditOrDebit;
        }

        // Add date to values if it was selected
        if (date.value) {
          values.date = date.value;
        }

        console.log(values);
        console.log(action.value);
        const payload = {
          data: values,
          action: action.value,
        };

        await store.dispatch(Actions.CREATE_CUSTOMER_ADJUSTMENT, payload);
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          if (submitButton.value) {
            submitButton.value.disabled = false;
          }
        } else {
          Swal.fire({
            text: translate('Successfully Created!'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          });
          resetForm();
          from.value = '';
          to.value = '';
          amount.value = 0;
          date.value = '';
          creditOrDebit.value = '';
          action.value = 'transfer';

          submitButton.value?.removeAttribute('data-kt-indicator');
          if (submitButton.value) {
            submitButton.value.disabled = false;
          }
        }
      };

      return {
        onSubmitCreate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        can,
        onStartDateChange,
        from,
        to,
        date,
        amount,
        actions,
        creditDebit,
        action,
        creditOrDebit,
        showCreditDebit,
        showToCustomer,
        customers: computed(() => store.getters.allCustomersList),
      };
    },
  });
